/**
 * SettingsSection component
 *
 * Wrapper component for a group of related settings.
 * Provides consistent section title, description, and bordered container.
 */

import { Box, Typography, Paper } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import { ReactNode, useId } from 'react';

interface SettingsSectionProps {
  /** Section title (e.g., "Actionable Notifications") */
  title: string;
  /** Optional description explaining the section */
  description?: string;
  /** Child components (typically SettingToggle components) */
  children: ReactNode;
}

/**
 * Section wrapper for grouped notification settings
 *
 * Features:
 * - Clear section title and description
 * - Bordered paper container with Capital Design System styling
 * - Consistent spacing and layout
 */
export function SettingsSection({ title, description, children }: SettingsSectionProps) {
  const titleId = useId();

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography id={titleId} variant="h6" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
      <Paper
        role="group"
        aria-labelledby={titleId}
        elevation={0}
        sx={{
          border: `1px solid ${capitalDesignTokens.semanticColors.border.primary}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}
